import React from 'react';
import { motion } from 'framer-motion';
import { Code2, Globe, Smartphone, Cpu, Zap } from 'lucide-react';

const services = [
  {
    id: 'custom-software',
    icon: Code2,
    title: 'Custom Software Development',
    description: 'Tailored software solutions built from scratch to fit your business needs. From complex enterprise systems to specialized tools.',
    featured: true,
  },
  {
    id: 'web-development',
    icon: Globe,
    title: 'Web Development',
    description: 'Modern, fast, and responsive websites that drive results. Built with cutting-edge technologies for optimal performance.',
    featured: false,
  },
  {
    id: 'mobile-development',
    icon: Smartphone,
    title: 'Mobile App Development',
    description: 'Native and cross-platform apps for iOS and Android. Beautiful, intuitive interfaces with powerful functionality.',
    featured: false,
  },
  {
    id: 'business-automation',
    icon: Cpu,
    title: 'Business Automation',
    description: 'AI-powered automations and workflows to streamline operations and save time. Transform manual processes into efficient systems.',
    featured: false,
  },
  {
    id: 'integrations',
    icon: Zap,
    title: 'Systems & Integrations',
    description: 'Connect your tools, APIs, and platforms into one seamless ecosystem. Eliminate data silos and improve efficiency.',
    featured: false,
  },
];

const ServicesSection = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' },
    },
  };

  return (
    <section
      id="services"
      className="relative py-20 md:py-32 overflow-hidden"
      data-testid="services-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-[#030305]" />
      <div className="absolute inset-0 grid-bg opacity-30" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-left mb-16"
        >
          <span className="text-cyan-400 text-sm font-mono tracking-wider uppercase mb-4 block">
            Our Services
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4" data-testid="services-heading">
            Software Development Miami
          </h2>
          <p className="text-gray-400 text-base md:text-lg max-w-2xl">
            End-to-end software development services for businesses in Miami and South Florida. 
            We turn your vision into reality with custom web applications and mobile solutions.
          </p>
        </motion.div>

        {/* Services Bento Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={service.id}
                variants={itemVariants}
                className={`service-card bg-white/5 border border-white/10 rounded-2xl p-8 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-violet-900/20 group relative overflow-hidden ${
                  service.featured ? 'md:col-span-2 lg:col-span-1 lg:row-span-2' : ''
                }`}
                data-testid={`service-card-${service.id}`}
              >
                {/* Gradient overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-violet-600/0 to-cyan-500/0 group-hover:from-violet-600/5 group-hover:to-cyan-500/5 transition-all duration-500" />

                <div className="relative z-10">
                  {/* Icon */}
                  <div className="icon-container w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-transform group-hover:scale-110">
                    <Icon className="w-7 h-7 text-violet-400 group-hover:text-cyan-400 transition-colors" strokeWidth={1.5} />
                  </div>

                  {/* Content */}
                  <h3 className="text-xl md:text-2xl font-semibold text-white mb-3 group-hover:text-violet-300 transition-colors">
                    {service.title}
                  </h3>
                  <p className={`text-gray-400 leading-relaxed ${service.featured ? 'text-base' : 'text-sm'}`}>
                    {service.description}
                  </p>

                  {/* Learn more link */}
                  <div className="mt-6 flex items-center text-violet-400 group-hover:text-cyan-400 transition-colors">
                    <span className="text-sm font-medium">Learn more</span>
                    <svg
                      className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                </div>

                {/* Corner decoration */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-violet-600/10 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity" />
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
